/**
 * Upgrade Prompt Modal
 * Displays when user hits tier limits, encouraging upgrade
 */
import { useAuthStore } from '../stores/authStore';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
  limitType: 'clipboard' | 'conversation';
  currentCount: number;
  limit: number;
}

const TIER_INFO = {
  basic: { name: 'Basic', color: 'text-blue-400' },
  advanced: { name: 'Advanced', color: 'text-purple-400' },
  pro: { name: 'Pro', color: 'text-orange-400' },
};

const TIER_FEATURES = {
  basic: {
    clipboard: 25,
    conversations: 25,
    price: 'Free',
  },
  advanced: {
    clipboard: 50,
    conversations: 50,
    price: '$9.99/month',
  },
  pro: {
    clipboard: 100,
    conversations: 100,
    price: '$19.99/month',
  },
};

export default function UpgradePrompt({
  isOpen,
  onClose,
  onUpgrade,
  limitType,
  currentCount,
  limit,
}: UpgradePromptProps) {
  const { user } = useAuthStore();

  if (!isOpen) return null;

  const currentTier = user?.subscription_tier || 'basic';
  const limitName = limitType === 'clipboard' ? 'Bridge items' : 'conversations';

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-dark-card border border-dark-border rounded-lg max-w-2xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-dark-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-dark-text mb-2">
                Limit Reached
              </h2>
              <p className="text-dark-text-muted">
                You've reached your {limitName} limit ({currentCount}/{limit})
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-dark-text-muted hover:text-dark-text transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Current Plan */}
        <div className="p-6 bg-dark-bg/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="px-3 py-1 bg-accent-blue/20 border border-accent-blue/30 rounded-lg">
              <span className="text-sm font-bold text-accent-blue uppercase">
                Current Plan: {TIER_INFO[currentTier as keyof typeof TIER_INFO]?.name || 'Basic'}
              </span>
            </div>
          </div>
          <p className="text-dark-text-muted text-sm">
            To continue adding {limitName}, please upgrade your plan or delete some existing items.
          </p>
        </div>

        {/* Tier Comparison */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-dark-text mb-4">Available Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(TIER_FEATURES).map(([tier, features]) => {
              const isCurrentTier = tier === currentTier;
              const tierInfo = TIER_INFO[tier as keyof typeof TIER_INFO];

              return (
                <div
                  key={tier}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isCurrentTier
                      ? 'border-accent-blue bg-accent-blue/10'
                      : 'border-dark-border hover:border-accent-blue/50'
                  }`}
                >
                  <div className="mb-3">
                    <h4 className={`text-lg font-bold ${tierInfo?.color || 'text-dark-text'}`}>
                      {tierInfo?.name}
                    </h4>
                    <p className="text-2xl font-bold text-dark-text mt-1">{features.price}</p>
                  </div>
                  <ul className="space-y-2 text-sm text-dark-text-muted">
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {features.clipboard} Bridge items
                    </li>
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {features.conversations} conversations
                    </li>
                  </ul>
                  {isCurrentTier && (
                    <div className="mt-3 px-2 py-1 bg-accent-blue/20 rounded text-xs text-accent-blue font-semibold text-center">
                      Current Plan
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-dark-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-dark-bg border border-dark-border text-dark-text rounded-lg hover:bg-dark-sidebar transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onClose();
              if (onUpgrade) {
                onUpgrade();
              } else {
                window.dispatchEvent(new CustomEvent('navigate-view', { detail: 'subscription' }));
              }
            }}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-accent-blue to-accent-orange text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-accent-blue/40 transition-all"
          >
            Upgrade Plan
          </button>
        </div>
      </div>
    </div>
  );
}
